import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import { clearUser } from '../feature/user.Slice';

/**
 * @file
 * @module Pages/AdminEmailChanges
 * @description Page admin listant l'historique des changements d'email (utilisateur et admin).
 */
const apiPrefix = process.env.REACT_APP_API_PREFIX;

const AdminEmailChanges = () => {
    const token = useSelector((state) => state.user.token);
    const dispatch = useDispatch();
    const [items, setItems] = useState([]);
    const [source, setSource] = useState('');
    const [page, setPage] = useState(1);
    const [total, setTotal] = useState(0);
    const [limit] = useState(50);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchChanges = async () => {
            setLoading(true);
            setError(null);
            try {
                const params = new URLSearchParams({ page: String(page), limit: String(limit) });
                if (source) {
                    params.set('source', source);
                }
                const response = await fetch(`${apiPrefix}admin/email-changes?${params.toString()}`, {
                    method: 'GET',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json',
                    },
                });

                if (!response.ok) {
                    if (response.status === 401 || response.status === 403) {
                        dispatch(clearUser());
                    }
                    throw new Error('Erreur lors de la récupération de l\'historique');
                }

                const data = await response.json();
                setItems(data.items || []);
                setTotal(data.total || 0);
            } catch (err) {
                console.error('Erreur:', err);
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        fetchChanges();
    }, [apiPrefix, token, dispatch, source, page, limit]);

    const totalPages = Math.max(1, Math.ceil(total / limit));

    const formatUser = (person) => {
        if (!person) return '—';
        const name = `${person.firstname || ''} ${person.lastname || ''}`.trim();
        return name ? `${name} (${person.username})` : person.username;
    };

    return (
        <div className="container-responsive section-padding">
            <div className="max-w-7xl mx-auto">
                <div className="mb-6">
                    <Link to="/dashboard" className="btn-secondary inline-flex items-center">
                        <i className="fas fa-arrow-left mr-2"></i>
                        Retour au tableau de bord
                    </Link>
                </div>

                <div className="card mb-6">
                    <h1 className="text-2xl font-bold text-primary mb-4">Historique des changements d'email</h1>
                    <p className="text-gray-600 mb-4">
                        Tous les remplacements d'adresse, qu'ils viennent du profil utilisateur ou d'un administrateur.
                    </p>
                    <div className="flex flex-wrap gap-2">
                        {[
                            { value: '', label: 'Tous' },
                            { value: 'user', label: 'Utilisateur' },
                            { value: 'admin', label: 'Admin' }
                        ].map((option) => (
                            <button
                                key={option.value || 'all'}
                                type="button"
                                className={source === option.value ? 'btn-primary' : 'btn-secondary'}
                                onClick={() => {
                                    setPage(1);
                                    setSource(option.value);
                                }}
                            >
                                {option.label}
                            </button>
                        ))}
                    </div>
                </div>

                {loading && (
                    <div className="flex justify-center items-center min-h-[200px]">
                        <div className="loading-spinner"></div>
                        <span className="ml-3 text-gray-600">Chargement de l'historique...</span>
                    </div>
                )}

                {error && (
                    <div className="alert alert-danger">
                        <i className="fas fa-exclamation-triangle mr-2"></i>
                        {error}
                    </div>
                )}

                {!loading && !error && (
                    <div className="card">
                        <div className="overflow-x-auto">
                            <table className="table">
                                <thead>
                                    <tr>
                                        <th>Date</th>
                                        <th>Compte</th>
                                        <th>Ancien email</th>
                                        <th>Nouvel email</th>
                                        <th>Origine</th>
                                        <th>Admin</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {items.length === 0 && (
                                        <tr>
                                            <td colSpan="6" className="text-center text-gray-500 py-6">
                                                Aucun changement d'email sur la période conservée (12 mois).
                                            </td>
                                        </tr>
                                    )}
                                    {items.map((item) => (
                                        <tr key={item.id}>
                                            <td className="text-gray-500 text-sm whitespace-nowrap">
                                                {item.createdAt ? new Date(item.createdAt).toLocaleString('fr-FR') : ''}
                                            </td>
                                            <td>{formatUser(item.user)}</td>
                                            <td className="font-medium">{item.oldEmail}</td>
                                            <td className="font-medium">{item.newEmail}</td>
                                            <td>
                                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                                    item.source === 'admin'
                                                        ? 'bg-yellow-100 text-yellow-800'
                                                        : 'bg-blue-100 text-blue-800'
                                                }`}>
                                                    {item.source === 'admin' ? 'Admin' : 'Utilisateur'}
                                                </span>
                                            </td>
                                            <td>{item.source === 'admin' ? formatUser(item.admin) : '—'}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        {totalPages > 1 && (
                            <div className="flex justify-between items-center mt-4">
                                <button
                                    type="button"
                                    className="btn-secondary"
                                    disabled={page <= 1}
                                    onClick={() => setPage((current) => Math.max(1, current - 1))}
                                >
                                    Précédent
                                </button>
                                <span className="text-gray-600 text-sm">
                                    Page {page} / {totalPages} ({total} changement{total > 1 ? 's' : ''})
                                </span>
                                <button
                                    type="button"
                                    className="btn-secondary"
                                    disabled={page >= totalPages}
                                    onClick={() => setPage((current) => current + 1)}
                                >
                                    Suivant
                                </button>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default AdminEmailChanges;
